import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  MapPin, 
  AlertTriangle, 
  Shield, 
  Loader2, 
  RefreshCw,
  Building2,
  Zap,
  Cloud,
  Waves,
  Mountain,
  Factory,
  Wind
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icons
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Color coding by location type
const LOCATION_TYPE_COLORS = {
  headquarters: "#3B82F6",      // Blue
  branch: "#10B981",           // Green
  datacenter: "#8B5CF6",       // Purple
  warehouse: "#F59E0B",        // Amber
  backoffice: "#F97316",       // Orange
  hospital: "#EF4444",         // Red
  clinic: "#EC4899",           // Pink
  call_center: "#06B6D4",      // Cyan
  manufacturing: "#84CC16",    // Lime
  retail: "#14B8A6",           // Teal
  other: "#6B7280",            // Gray
  default: "#6366F1"           // Indigo
};

const getLocationTypeColor = (type) => {
  return LOCATION_TYPE_COLORS[type] || LOCATION_TYPE_COLORS.default;
};

const getLocationTypeLabel = (type) => {
  const labels = {
    headquarters: "Headquarters",
    branch: "Branch Office",
    datacenter: "Data Center",
    warehouse: "Warehouse",
    backoffice: "Back Office",
    hospital: "Hospital Facility",
    clinic: "Clinic",
    call_center: "Call Center",
    manufacturing: "Manufacturing Plant",
    retail: "Retail Store",
    other: "Other"
  };
  return labels[type] || type;
};

// Map center updater component
function MapCenterUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 12);
    }
  }, [center, map]);
  return null;
}

const getRiskIcon = (riskType) => {
  const icons = {
    "Flood Risk": Waves,
    "Hurricane Zone": Wind,
    "Seismic Activity": Mountain,
    "Tornado Risk": Cloud,
    "Extreme Cold": Cloud,
    "Urban Density": Building2,
    "Industrial Proximity": Factory,
    "Coastal Exposure": Waves,
  };
  return icons[riskType] || AlertTriangle;
};

const getSeverityColor = (severity) => {
  switch (severity) {
    case "high": return "bg-red-100 text-red-700 border-red-200";
    case "medium": return "bg-amber-100 text-amber-700 border-amber-200";
    case "low": return "bg-blue-100 text-blue-700 border-blue-200";
    default: return "bg-slate-100 text-slate-700 border-slate-200";
  }
};

const getOverallRiskColor = (risk) => {
  switch (risk) {
    case "high": return "destructive";
    case "medium": return "secondary";
    case "low": return "default";
    default: return "outline";
  }
};

export default function GeospatialRiskMap({ 
  locations = [], 
  onAnalyzeLocation,
  showAnalysis = true,
  height = "400px"
}) {
  const { api } = useAuth();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]); // US center default

  useEffect(() => {
    // Set map center to first location if available
    if (locations.length > 0 && locations[0].latitude && locations[0].longitude) {
      setMapCenter([locations[0].latitude, locations[0].longitude]);
    }
  }, [locations]);

  const analyzeLocationRisk = async (location) => {
    if (!location.latitude || !location.longitude) {
      return;
    }
    
    setSelectedLocation(location);
    setAnalyzing(true);
    
    try {
      const response = await api.post("/ai-assistant/analyze-location-risks", {
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address || location.name
      });
      
      if (response.data?.success) {
        setRiskAnalysis(response.data.analysis);
        onAnalyzeLocation?.(location, response.data.analysis);
      }
    } catch (error) {
      console.error("Risk analysis failed:", error);
    } finally {
      setAnalyzing(false);
    }
  };

  const getMarkerColor = (location) => {
    // First check location type for color coding
    if (location.type) {
      return getLocationTypeColor(location.type);
    }
    // Fall back to risk level
    if (!location.riskLevel) return "#6366f1"; // Indigo default
    switch (location.riskLevel) {
      case "high": return "#ef4444";
      case "medium": return "#f59e0b";
      case "low": return "#22c55e";
      default: return "#6366f1";
    }
  };

  const activeLocationTypes = useMemo(() => 
    Object.entries(LOCATION_TYPE_COLORS).filter(([key]) => 
      key !== 'default' && locations.some(l => l.type === key)
    ), [locations]);

  return (
    <div className="space-y-4">
      {/* Map Container */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-sky-700" />
              Geospatial Risk Intelligence
            </CardTitle>
            {selectedLocation && (
              <Badge variant={getOverallRiskColor(riskAnalysis?.overall_risk)}>
                {riskAnalysis?.overall_risk?.toUpperCase() || "ANALYZING"} RISK
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div style={{ height }} className="relative" data-testid="geospatial-map-container">
            <MapContainer
              center={mapCenter}
              zoom={4}
              style={{ height: "100%", width: "100%", zIndex: 1 }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapCenterUpdater center={mapCenter} />
              
              {locations.map((location, index) => (
                location.latitude && location.longitude && (
                  <Marker
                    key={index}
                    position={[location.latitude, location.longitude]}
                    icon={createCustomIcon(getMarkerColor(location))}
                    eventHandlers={{
                      click: () => analyzeLocationRisk(location)
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[200px]">
                        {/* Location Header with Type Badge */}
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-bold text-sm">{location.name || "Location"}</p>
                          <span 
                            className="text-xs px-2 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: getMarkerColor(location) }}
                          >
                            {getLocationTypeLabel(location.type)}
                          </span>
                        </div>
                        
                        {/* Address */}
                        <p className="text-xs text-slate-600">{location.address}</p>
                        
                        {/* AI Summary Tooltip */}
                        {location.risk_analysis && (
                          <div className="mt-2 p-2 bg-slate-50 rounded text-xs">
                            <p className="font-semibold text-slate-700">AI Risk Summary:</p>
                            <p className="text-slate-600 mt-1">
                              Risk Level: <span className={`font-bold ${
                                location.risk_analysis.overall_risk === 'high' ? 'text-red-600' :
                                location.risk_analysis.overall_risk === 'medium' ? 'text-amber-600' :
                                'text-emerald-600'
                              }`}>{location.risk_analysis.overall_risk?.toUpperCase()}</span>
                            </p>
                            {location.risk_analysis.risks?.slice(0, 2).map((r, i) => (
                              <p key={i} className="text-slate-500 mt-0.5">• {r.type}</p>
                            ))}
                          </div>
                        )}
                        
                        <Button 
                          size="sm" 
                          className="mt-2 w-full h-7 text-xs"
                          onClick={() => analyzeLocationRisk(location)}
                          disabled={analyzing}
                        >
                          {analyzing ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <Shield className="w-3 h-3 mr-1" />
                          )}
                          {location.risk_analysis ? 'Refresh Analysis' : 'Analyze Risks'}
                        </Button>
                      </div>
                    </Popup>
                  </Marker>
                )
              ))}
            </MapContainer>
            
            {/* AI Analysis Button Overlay */}
            {locations.length > 0 && !showAnalysis && (
              <div className="absolute bottom-4 right-4 z-[1000]">
                <Button 
                  onClick={() => locations[0] && analyzeLocationRisk(locations[0])}
                  disabled={analyzing}
                  className="shadow-lg"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  AI Threat Analysis
                </Button>
              </div>
            )}
            
            {/* Color Legend */}
            {locations.length > 0 && (
              <div className="absolute top-4 right-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 max-w-[180px]">
                <p className="text-xs font-semibold text-slate-700 mb-2">Location Types</p>
                <div className="space-y-1.5">
                  {activeLocationTypes.map(([type, color]) => (
                    <div key={type} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-xs text-slate-600">{getLocationTypeLabel(type)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Risk Analysis Panel */}
      {showAnalysis && riskAnalysis && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                AI Risk Analysis: {selectedLocation?.name || selectedLocation?.address}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-white">
                  Score: {riskAnalysis.risk_score}/100
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => selectedLocation && analyzeLocationRisk(selectedLocation)}
                  disabled={analyzing}
                >
                  <RefreshCw className={`w-4 h-4 ${analyzing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Identified Risks */}
            {riskAnalysis.risks?.length > 0 ? (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-700">Identified Threats</h4>
                <div className="grid gap-2">
                  {riskAnalysis.risks.map((risk, idx) => {
                    const RiskIcon = getRiskIcon(risk.type);
                    return (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${getSeverityColor(risk.severity)}`}
                      >
                        <div className="flex items-start gap-3">
                          <RiskIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{risk.type}</span>
                              <Badge variant="outline" className={getSeverityColor(risk.severity)}>
                                {risk.severity}
                              </Badge>
                            </div>
                            <p className="text-xs mt-1 opacity-80">{risk.description}</p>
                            <p className="text-xs mt-2 font-medium">
                              <span className="opacity-70">Mitigation:</span> {risk.mitigation}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <Shield className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-emerald-800">No significant risks identified</p>
                <p className="text-xs text-emerald-600 mt-1">This location has a favorable risk profile</p>
              </div>
            )}

            {/* Recommendations */}
            {riskAnalysis.recommendations?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-700">Recommendations</h4>
                <ul className="space-y-1.5">
                  {riskAnalysis.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                      <span className="text-emerald-600 mt-0.5">✓</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
